import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Globe, X, MapPin } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-50 text-veg border-veg/30" },
  coming_soon: { label: "Coming Soon", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  not_available: { label: "Not Available", cls: "bg-ink/5 text-ink-600 border-border" },
};
const EMPTY = { country: "India", state: "", city: "", area: "", status: "coming_soon", currency: "INR", phone_code: "+91", notes: "" };
const inp = "w-full bg-surface rounded-lg px-3 py-2 text-sm outline-none border border-border";
const L = ({ label, children }) => (<div><label className="text-[11px] font-semibold text-ink-600 mb-0.5 block">{label}</label>{children}</div>);

export default function AdminLocations() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/locations").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.country?.trim()) return toast.error("Country is required");
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/locations/${form.id}`, form);
      else await api.post("/admin/locations", form);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/admin/locations/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4" data-testid="admin-locations">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2"><Globe size={20} className="text-coral" /><h2 className="font-display font-bold text-xl text-ink">Service Locations</h2></div>
        <button onClick={() => setForm({ ...EMPTY })} className="ml-auto flex items-center gap-1.5 bg-coral text-white font-bold text-sm px-3 py-2 rounded-xl tap" data-testid="add-location"><Plus size={16} /> Add location</button>
      </div>
      <div className="bg-coral-light border border-coral/20 rounded-2xl p-4 text-sm text-ink-700">
        Only <strong>Active</strong> locations are treated as live delivery areas. Country → State/Region → City → Service Area. Currency and phone code are stored for future international expansion.
      </div>

      {list.length === 0 && <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-600">No locations yet. Add your first market.</div>}

      <div className="grid gap-3 md:grid-cols-2">
        {list.map((l) => (
          <div key={l.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`location-${l.id}`}>
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-xl bg-coral-light flex items-center justify-center shrink-0"><MapPin size={17} className="text-coral" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-ink">{[l.city, l.area].filter(Boolean).join(" · ") || l.state || l.country}</div>
                <div className="text-xs text-ink-600">{[l.state, l.country].filter(Boolean).join(", ")} · {l.currency} · {l.phone_code}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${STATUS[l.status]?.cls || STATUS.not_available.cls}`}>{STATUS[l.status]?.label || l.status}</span>
            </div>
            {l.notes && <div className="text-xs text-ink-600 mt-2 italic">{l.notes}</div>}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
              <button onClick={() => setForm({ ...l })} className="ml-auto text-ink-600 text-xs font-semibold flex items-center gap-1" data-testid={`location-edit-${l.id}`}><Pencil size={13} /> Edit</button>
              <button onClick={() => del(l.id)} className="text-nonveg text-xs font-semibold flex items-center gap-1" data-testid={`location-del-${l.id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="location-form">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg text-ink">{form.id ? "Edit" : "Add"} location</h3><button onClick={() => setForm(null)}><X size={20} /></button></div>
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <L label="Country *"><input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className={inp} data-testid="lf-country" /></L>
                <L label="State / Region"><input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className={inp} /></L>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <L label="City"><input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className={inp} data-testid="lf-city" /></L>
                <L label="Service area"><input value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} className={inp} /></L>
              </div>
              <L label="Status"><select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className={inp} data-testid="lf-status"><option value="active">Active</option><option value="coming_soon">Coming Soon</option><option value="not_available">Not Available</option></select></L>
              <div className="grid grid-cols-2 gap-2">
                <L label="Currency"><input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={inp} placeholder="INR" /></L>
                <L label="Phone code"><input value={form.phone_code} onChange={(e) => setForm((f) => ({ ...f, phone_code: e.target.value }))} className={inp} placeholder="+91" /></L>
              </div>
              <L label="Notes"><textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className={inp} /></L>
            </div>
            <button onClick={save} disabled={saving} className="w-full bg-coral text-white font-bold py-2.5 rounded-xl mt-4 tap disabled:opacity-60" data-testid="lf-save">{saving ? "Saving…" : "Save location"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
